/**
 * Workspace types describe what kind of organisation runs a workspace. A type
 * sets which tabs appear, what the home screen offers, what roles and records
 * are called, and which setup steps are shown. Super Admin can change every
 * type through app_config.saas_workspace_types, and each organisation can
 * rename terms and role titles for itself.
 */

export const WORKSPACE_TYPE_IDS = ['services', 'products', 'association', 'club', 'training'] as const;
export type WorkspaceTypeId = (typeof WORKSPACE_TYPE_IDS)[number];

export const TERM_KEYS = [
  'customer',
  'customers',
  'booking',
  'bookings',
  'order',
  'orders',
  'product',
  'products',
  'membership',
  'memberships',
  'application',
  'applications',
  'invoice',
  'invoices',
] as const;
export type TermKey = (typeof TERM_KEYS)[number];
export type Terms = Record<TermKey, string>;

export const ROLE_KEYS = ['super_admin', 'admin', 'manager', 'supervisor', 'employee', 'customer'] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];
export type RoleTitles = Record<RoleKey, string>;

export type HomeActionTarget =
  | { kind: 'tab'; tab: string }
  | { kind: 'link'; link: 'book' | 'store' | 'apply' };

export type HomeAction = { label: string; target: HomeActionTarget };

export type TryAppPreview = { role: 'admin' | 'supervisor' | 'customer'; why: string };

export type StarterRole = {
  name: string;
  description: string;
  baseRole: 'admin' | 'manager';
  moduleKeys: string[];
};

export type CustomerLanding = 'book' | 'store' | 'membership';

/** What a customer's own account is for. `{org}` in the pitch becomes the organisation's name. */
export type CustomerAccountCopy = { pitch: string; landing: CustomerLanding };

export type WorkspaceTypeDefinition = {
  id: WorkspaceTypeId;
  label: string;
  description: string;
  customerAccount: CustomerAccountCopy;
  sells: { services: boolean; products: boolean; memberships: boolean };
  membershipApplications: boolean;
  terms: Partial<Terms>;
  roles: Partial<RoleTitles>;
  home: { title: string; why: string; actions: HomeAction[] };
  tryApps: TryAppPreview[];
  checklist: string[];
  starterRoles: StarterRole[];
};

export const DEFAULT_TERMS: Terms = {
  customer: 'Customer',
  customers: 'Customers',
  booking: 'Booking',
  bookings: 'Bookings',
  order: 'Order',
  orders: 'Orders',
  product: 'Product',
  products: 'Products',
  membership: 'Membership',
  memberships: 'Memberships',
  application: 'Application',
  applications: 'Applications',
  invoice: 'Invoice',
  invoices: 'Invoices',
};

export const DEFAULT_ROLE_TITLES: RoleTitles = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  supervisor: 'Supervisor',
  employee: 'Worker',
  customer: 'Customer',
};

export const BUILT_IN_WORKSPACE_TYPES: Record<WorkspaceTypeId, WorkspaceTypeDefinition> = {
  services: {
    id: 'services',
    label: 'Service company',
    description: 'Bookings, jobs, crews, quotes and invoices.',
    customerAccount: { pitch: 'Book faster and track your jobs with {org}.', landing: 'book' },
    sells: { services: true, products: false, memberships: false },
    membershipApplications: false,
    terms: {},
    roles: {},
    home: {
      title: 'Take a booking when you are ready',
      why: 'There is nothing to report yet. Place a test booking, or finish the details in Your business.',
      actions: [{ label: 'Take a booking', target: { kind: 'link', link: 'book' } }],
    },
    tryApps: [
      { role: 'admin', why: 'Bookings, people and money — what an office manager uses.' },
      { role: 'supervisor', why: 'Today’s jobs and crew — what a team lead uses.' },
      { role: 'customer', why: 'Book, pay and track — what your customer sees.' },
    ],
    checklist: ['company-profile', 'services-pricing', 'invite-team'],
    starterRoles: [],
  },
  products: {
    id: 'products',
    label: 'Product seller',
    description: 'Products, stock, orders and an online store.',
    customerAccount: { pitch: 'Order faster and follow your orders with {org}.', landing: 'store' },
    sells: { services: false, products: true, memberships: false },
    membershipApplications: false,
    terms: {},
    roles: { employee: 'Staff' },
    home: {
      title: 'Make your first sale',
      why: 'Add a product, then share your store or create an order yourself.',
      actions: [
        { label: 'Add a product', target: { kind: 'tab', tab: 'products' } },
        { label: 'Open your store', target: { kind: 'link', link: 'store' } },
      ],
    },
    tryApps: [
      { role: 'admin', why: 'Orders, products and payments — what the office team uses.' },
      { role: 'customer', why: 'Their orders and invoices — what your customers see.' },
    ],
    checklist: ['company-profile', 'catalog-products', 'online-store', 'invite-team'],
    starterRoles: [
      {
        name: 'Store manager',
        description: 'Runs products, stock and orders.',
        baseRole: 'manager',
        moduleKeys: ['overview', 'products', 'orders', 'online-store', 'procurement', 'customers', 'payments'],
      },
    ],
  },
  association: {
    id: 'association',
    label: 'Membership association',
    description: 'Applications with document review, members and renewals.',
    customerAccount: {
      pitch: 'Renew your membership, keep your documents up to date and pay invoices with {org}.',
      landing: 'membership',
    },
    sells: { services: false, products: false, memberships: true },
    membershipApplications: true,
    terms: {
      customer: 'Member',
      customers: 'Members',
      product: 'Plan',
      products: 'Plans',
      order: 'Invoice',
      orders: 'Invoices',
    },
    roles: {
      super_admin: 'President',
      admin: 'Secretary',
      manager: 'Treasurer',
      supervisor: 'Committee member',
      employee: 'Office staff',
      customer: 'Member',
    },
    home: {
      title: 'Welcome your first member',
      why: 'Share the application form. New applications appear under Memberships → Applications for review.',
      actions: [
        { label: 'Review applications', target: { kind: 'tab', tab: 'memberships' } },
        { label: 'Open the application form', target: { kind: 'link', link: 'apply' } },
      ],
    },
    tryApps: [
      { role: 'admin', why: 'Applications, members and payments — what the office team uses.' },
      { role: 'customer', why: 'Their membership, invoices and renewals — what your members see.' },
    ],
    checklist: ['company-profile', 'catalog-plans', 'membership-applications', 'online-store', 'invite-team'],
    starterRoles: [
      {
        name: 'Treasurer',
        description: 'Invoices, payments and reports.',
        baseRole: 'manager',
        moduleKeys: ['overview', 'orders', 'payments', 'memberships', 'reports'],
      },
      {
        name: 'Committee member',
        description: 'Reviews applications and sees the member list.',
        baseRole: 'manager',
        moduleKeys: ['overview', 'memberships', 'customers'],
      },
    ],
  },
  club: {
    id: 'club',
    label: 'Club or subscription',
    description: 'Members, plans and renewals, without document review.',
    customerAccount: { pitch: 'Manage your membership and renewals with {org}.', landing: 'membership' },
    sells: { services: false, products: false, memberships: true },
    membershipApplications: false,
    terms: {
      customer: 'Member',
      customers: 'Members',
      product: 'Plan',
      products: 'Plans',
    },
    roles: { super_admin: 'Owner', admin: 'Club admin', employee: 'Staff', customer: 'Member' },
    home: {
      title: 'Welcome your first member',
      why: 'Share your membership store, or add a member order yourself.',
      actions: [
        { label: 'Open memberships', target: { kind: 'tab', tab: 'memberships' } },
        { label: 'Open your store', target: { kind: 'link', link: 'store' } },
      ],
    },
    tryApps: [
      { role: 'admin', why: 'Members, renewals and payments — what the front desk uses.' },
      { role: 'customer', why: 'Their plan, invoices and renewals — what your members see.' },
    ],
    checklist: ['company-profile', 'catalog-plans', 'online-store', 'invite-team'],
    starterRoles: [
      {
        name: 'Front desk',
        description: 'Members, renewals and payments.',
        baseRole: 'manager',
        moduleKeys: ['overview', 'memberships', 'customers', 'orders', 'payments'],
      },
    ],
  },
  training: {
    id: 'training',
    label: 'Training or events',
    description: 'Courses and events sold online, with registrations.',
    customerAccount: { pitch: 'Register for courses and events and keep your bookings with {org}.', landing: 'store' },
    sells: { services: false, products: true, memberships: false },
    membershipApplications: false,
    terms: {
      customer: 'Participant',
      customers: 'Participants',
      product: 'Course',
      products: 'Courses',
      order: 'Registration',
      orders: 'Registrations',
    },
    roles: { admin: 'Coordinator', employee: 'Trainer', customer: 'Participant' },
    home: {
      title: 'Open your first course',
      why: 'Add a course or event with a price, then share your store so people can register.',
      actions: [
        { label: 'Add a course', target: { kind: 'tab', tab: 'products' } },
        { label: 'Open your store', target: { kind: 'link', link: 'store' } },
      ],
    },
    tryApps: [
      { role: 'admin', why: 'Courses, registrations and payments — what the coordinator uses.' },
      { role: 'customer', why: 'Their registrations and invoices — what participants see.' },
    ],
    checklist: ['company-profile', 'catalog-products', 'online-store', 'invite-team'],
    starterRoles: [
      {
        name: 'Course coordinator',
        description: 'Courses, registrations and participant messages.',
        baseRole: 'manager',
        moduleKeys: ['overview', 'products', 'orders', 'customers', 'communication', 'payments'],
      },
    ],
  },
};

export function isWorkspaceTypeId(value: unknown): value is WorkspaceTypeId {
  return typeof value === 'string' && (WORKSPACE_TYPE_IDS as readonly string[]).includes(value);
}

function stringRecord<K extends string>(raw: unknown, keys: readonly K[]): Partial<Record<K, string>> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) out[key] = value.trim().slice(0, 60);
  }
  return out;
}

function stringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((item): item is string => typeof item === 'string' && item.trim() !== '').map((item) => item.trim());
}

function parseHome(raw: unknown, fallback: WorkspaceTypeDefinition['home']): WorkspaceTypeDefinition['home'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fallback;
  const row = raw as Record<string, unknown>;
  const actions = Array.isArray(row.actions)
    ? row.actions
        .map((item): HomeAction | null => {
          if (!item || typeof item !== 'object') return null;
          const action = item as Record<string, unknown>;
          const label = typeof action.label === 'string' ? action.label.trim() : '';
          if (!label) return null;
          if (typeof action.tab === 'string' && action.tab.trim()) {
            return { label, target: { kind: 'tab', tab: action.tab.trim() } };
          }
          if (action.link === 'book' || action.link === 'store' || action.link === 'apply') {
            return { label, target: { kind: 'link', link: action.link } };
          }
          return null;
        })
        .filter((item): item is HomeAction => item !== null)
    : fallback.actions;
  return {
    title: typeof row.title === 'string' && row.title.trim() ? row.title.trim() : fallback.title,
    why: typeof row.why === 'string' && row.why.trim() ? row.why.trim() : fallback.why,
    actions: actions.length ? actions : fallback.actions,
  };
}

function parseStarterRoles(raw: unknown, fallback: StarterRole[]): StarterRole[] {
  if (!Array.isArray(raw)) return fallback;
  return raw
    .map((item): StarterRole | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const name = typeof row.name === 'string' ? row.name.trim() : '';
      const moduleKeys = stringList(row.moduleKeys) ?? [];
      if (!name || moduleKeys.length === 0) return null;
      return {
        name: name.slice(0, 80),
        description: typeof row.description === 'string' ? row.description.trim() : '',
        baseRole: row.baseRole === 'admin' ? 'admin' : 'manager',
        moduleKeys,
      };
    })
    .filter((item): item is StarterRole => item !== null);
}

/** Merge Super Admin changes from app_config.saas_workspace_types over the built-in types. */
const CUSTOMER_LANDINGS: readonly CustomerLanding[] = ['book', 'store', 'membership'];

function parseCustomerAccount(raw: unknown, base: CustomerAccountCopy): CustomerAccountCopy {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const row = raw as Record<string, unknown>;
  return {
    pitch: typeof row.pitch === 'string' && row.pitch.trim() ? row.pitch.trim() : base.pitch,
    landing: CUSTOMER_LANDINGS.includes(row.landing as CustomerLanding) ? (row.landing as CustomerLanding) : base.landing,
  };
}

/** The customer-account pitch with the organisation's name filled in. */
export function customerAccountPitch(copy: CustomerAccountCopy, orgName: string): string {
  return copy.pitch.split('{org}').join(orgName);
}

export function parseWorkspaceTypeOverrides(raw: unknown): Record<WorkspaceTypeId, WorkspaceTypeDefinition> {
  const out = { ...BUILT_IN_WORKSPACE_TYPES };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const id of WORKSPACE_TYPE_IDS) {
    const patch = (raw as Record<string, unknown>)[id];
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) continue;
    const row = patch as Record<string, unknown>;
    const base = BUILT_IN_WORKSPACE_TYPES[id];
    out[id] = {
      ...base,
      label: typeof row.label === 'string' && row.label.trim() ? row.label.trim() : base.label,
      description:
        typeof row.description === 'string' && row.description.trim() ? row.description.trim() : base.description,
      customerAccount: parseCustomerAccount(row.customerAccount, base.customerAccount),
      terms: { ...base.terms, ...stringRecord(row.terms, TERM_KEYS) },
      roles: { ...base.roles, ...stringRecord(row.roles, ROLE_KEYS) },
      home: parseHome(row.home, base.home),
      checklist: stringList(row.checklist) ?? base.checklist,
      starterRoles: parseStarterRoles(row.starterRoles, base.starterRoles),
    };
  }
  return out;
}

/** Organisations created before workspace types existed get types from what they sell. */
export function workspaceTypesFromSellFlags(flags: {
  sellsServices: boolean;
  sellsProducts: boolean;
  sellsMemberships: boolean;
  membershipApplicationsRequired?: boolean;
}): WorkspaceTypeId[] {
  const types: WorkspaceTypeId[] = [];
  if (flags.sellsServices) types.push('services');
  if (flags.sellsProducts) types.push('products');
  if (flags.sellsMemberships) types.push(flags.membershipApplicationsRequired ? 'association' : 'club');
  return types.length ? types : ['services'];
}

export type WorkspaceProfile = {
  types: WorkspaceTypeId[];
  primary: WorkspaceTypeId;
  primaryDefinition: WorkspaceTypeDefinition;
  sells: { services: boolean; products: boolean; memberships: boolean };
  membershipApplications: boolean;
  terms: Terms;
  roles: RoleTitles;
  home: WorkspaceTypeDefinition['home'];
  tryApps: TryAppPreview[];
  checklist: string[];
  starterRoles: StarterRole[];
};

export function resolveWorkspaceProfile(
  input: {
    types: readonly string[];
    primary?: string | null;
    termOverrides?: unknown;
    roleOverrides?: unknown;
  },
  definitions: Record<WorkspaceTypeId, WorkspaceTypeDefinition> = BUILT_IN_WORKSPACE_TYPES,
): WorkspaceProfile {
  const types = input.types.filter(isWorkspaceTypeId).filter((id, index, list) => list.indexOf(id) === index);
  const chosen: WorkspaceTypeId[] = types.length ? types : ['services'];
  const primary: WorkspaceTypeId = isWorkspaceTypeId(input.primary) && chosen.includes(input.primary)
    ? input.primary
    : chosen[0];
  const primaryDefinition = definitions[primary];
  const all = chosen.map((id) => definitions[id]);

  const checklist: string[] = [];
  for (const definition of [primaryDefinition, ...all]) {
    for (const key of definition.checklist) if (!checklist.includes(key)) checklist.push(key);
  }
  const starterRoles: StarterRole[] = [];
  for (const definition of [primaryDefinition, ...all]) {
    for (const role of definition.starterRoles) {
      if (!starterRoles.some((item) => item.name === role.name)) starterRoles.push(role);
    }
  }

  return {
    types: chosen,
    primary,
    primaryDefinition,
    sells: {
      services: all.some((definition) => definition.sells.services),
      products: all.some((definition) => definition.sells.products),
      memberships: all.some((definition) => definition.sells.memberships),
    },
    membershipApplications: all.some((definition) => definition.membershipApplications),
    terms: { ...DEFAULT_TERMS, ...primaryDefinition.terms, ...stringRecord(input.termOverrides, TERM_KEYS) },
    roles: { ...DEFAULT_ROLE_TITLES, ...primaryDefinition.roles, ...stringRecord(input.roleOverrides, ROLE_KEYS) },
    home: primaryDefinition.home,
    tryApps: primaryDefinition.tryApps,
    checklist,
    starterRoles,
  };
}
